import { useEffect, useState } from 'react';
import api from '../api';
import { Link } from 'react-router-dom';
import { ModernCard } from './ui/modern';

function BranchList({ companyId, refreshSignal = 0 }) {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await api.get(`/companies/${companyId}/branches`);
        console.log('Branches data received:', response.data);
        setBranches(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching branches:', error);
        setLoading(false);
      }
    };
    fetchBranches();
  }, [companyId, refreshSignal]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-400">Loading branches...</div>
      </div>
    );
  }

  if (branches.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-slate-400 mb-2">No branches found for this company.</div>
        <div className="text-slate-500 text-sm">Add your first branch to get started.</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {branches.map((branch) => (
        <BranchCard key={branch.id} branch={branch} />
      ))}
    </div>
  );
}

// Branch Card Component
function BranchCard({ branch }) {
  // Get branch initial for avatar
  const initial = branch.name?.charAt(0)?.toUpperCase() || 'B';
  
  // Format address
  const addressParts = [
    branch.address,
    branch.city,
    branch.province,
    branch.postalCode,
    branch.country
  ].filter(Boolean);
  const address = addressParts.length > 0 ? addressParts.join(', ') : 'No address';

  // Calculate completion percentage (if branch has onboarding data)
  const totalUsers = branch.numberOfUsers || 0;
  const totalDevices = branch.numberOfDevices || 0;

  return (
    <Link to={`/branch/${branch.id}`} className="block group">
      <ModernCard hover={true} className="h-full">
        <div className="flex items-start gap-4 mb-4">
          {/* Branch Avatar */}
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-violet-500 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {initial}
          </div>

          {/* Branch Info */}
          <div className="flex-1 min-w-0">
            <h4 className="text-lg font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
              {branch.name}
            </h4>
            <p className="text-slate-400 text-sm line-clamp-1">
              {branch.city || 'No location'}, {branch.province || 'N/A'}
            </p>
          </div>

          {/* Arrow indicator */}
          <div className="text-slate-500 group-hover:text-white group-hover:translate-x-1 transition-all">
            →
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white/5 rounded-lg p-3 border border-white/5">
            <div className="text-2xl font-bold text-blue-400">{totalUsers}</div>
            <div className="text-slate-500 text-xs">Users</div>
          </div>
          <div className="bg-white/5 rounded-lg p-3 border border-white/5">
            <div className="text-2xl font-bold text-violet-400">{totalDevices}</div>
            <div className="text-slate-500 text-xs">Devices</div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-2 border-t border-white/5 pt-4">
          {branch.contactPerson && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">👤</span>
              <span className="text-slate-400">{branch.contactPerson}</span>
            </div>
          )}
          {branch.contactNumber && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">📞</span>
              <span className="text-slate-400">{branch.contactNumber}</span>
            </div>
          )}
          {branch.emailAddress && (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-500">✉️</span>
              <span className="text-slate-400 truncate">{branch.emailAddress}</span>
            </div>
          )}
        </div>
      </ModernCard>
    </Link>
  );
}

export default BranchList;
